import {
  DownloadIcon,
  SparksAIIcon,
  Typography,
  useFetchFile,
  useMobile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { Card, Progress, Spinner } from "@heroui/react";
import { useProfile } from "@hooks/useProfile";
import { GenerateResumeFileButton } from "@modules/Resume/components/GenerateResumeFileButton";
import React from "react";

interface ResumeGenerationCardProps {
  className?: string;
}

export const ResumeGenerationCard: React.FC<ResumeGenerationCardProps> = ({ className = "" }) => {
  const { profile, loading: getProfileIsLoading } = useProfile();
  const { cpjResumeFile, completionPercentage } = profile;
  const isMobile = useMobile();

  const { fetchFile: fetchCpjResumeBlob, loading: loadingCpjResumeBlob } = useFetchFile({
    urlPath: cpjResumeFile,
    downloadMode: true,
    cache: "no-cache",
    userType: UserTypeEnum.USER,
  });

  if (getProfileIsLoading) {
    return (
      <Card className={`${isMobile ? "p-3" : "p-4"} ${className}`}>
        <div className={`flex items-center justify-center ${isMobile ? "min-h-12" : "min-h-16"}`}>
          <Spinner size="sm" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={`${isMobile ? "p-3" : "p-4"} bg-white border border-gray-200 ${className}`}>
      <div className="flex flex-col space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div
            className={`${isMobile ? "w-6 h-6" : "w-7 h-7"} bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0`}
          >
            <SparksAIIcon className={`${isMobile ? "w-3 h-3" : "w-4 h-4"} stroke-gray-600`} viewBox="0 0 22 22" />
          </div>
          <div>
            <Typography className={`${isMobile ? "text-sm" : "text-base"} font-medium text-gray-900`}>
              Resume
            </Typography>
            <Typography className={`${isMobile ? "text-xs" : "text-sm"} text-gray-500`}>
              Profile completion & resume management
            </Typography>
          </div>
        </div>

        {/* Profile Completion Progress - Compact */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Typography className={`${isMobile ? "text-xs" : "text-sm"} text-gray-600`}>Profile Complete</Typography>
            <Typography className={`${isMobile ? "text-xs" : "text-sm"} text-gray-700 font-medium`}>
              {Math.round(completionPercentage)}%
            </Typography>
          </div>
          <Progress
            aria-label="Profile completion"
            value={completionPercentage}
            minValue={0}
            maxValue={100}
            className="h-1.5"
            classNames={{
              track: "bg-gray-200",
              indicator: "bg-primary-500",
            }}
          />
        </div>

        {/* Current Resume Info - Compact */}
        {cpjResumeFile && (
          <div className="flex items-center justify-between text-xs text-gray-600 py-1">
            <span>Current:</span>
            <div
              onClick={fetchCpjResumeBlob}
              className="flex items-center underline gap-2 text-blue-600 cursor-pointer hover:text-blue-700 truncate max-w-56"
            >
              <DownloadIcon className="w-4 h-4 fill-blue-600" />
              {!loadingCpjResumeBlob ? cpjResumeFile?.split("/")?.at(-1) : <Spinner size="sm" />}
            </div>
          </div>
        )}

        <div className="!mt-4">
          <GenerateResumeFileButton hasResume={!!cpjResumeFile} variant="prominent" />
        </div>
      </div>
    </Card>
  );
};
